import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import { useNotifications } from '../context/NotificationContext';
import { useT } from '../context/LanguageContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { apiFetch } from '../utils/api';
import AiAssistantPanel from './AiAssistantPanel';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const { counts, clearSection } = useNotifications();
  const t = useT();
  const { theme, themeId, setThemeId } = useTheme();

  // Absence warning panel state
  const [absenceAlerts, setAbsenceAlerts] = useState([]);
  const [absencePanelOpen, setAbsencePanelOpen] = useState(false);
  const [absenceCreditInputs, setAbsenceCreditInputs] = useState({});
  const [absenceGranting, setAbsenceGranting] = useState({});
  const [absenceDismissing, setAbsenceDismissing] = useState({});

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      document.body.classList.add('tg-webapp');
    }
    return () => document.body.classList.remove('tg-webapp');
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await apiFetch('/notifications/unread-count');
        const data = await res.json();
        if (data.success) setNotifUnread(data.data.count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAbsence = async () => {
      try {
        const res = await apiFetch('/alerts?unreadOnly=true');
        const data = await res.json();
        if (data.success) setAbsenceAlerts(data.data.filter(a => a.type === 'ABSENCE_WARNING'));
      } catch {}
    };
    fetchAbsence();
    const interval = setInterval(fetchAbsence, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleAbsenceGrantCredit = async (id) => {
    const amount = parseFloat(absenceCreditInputs[id] || '0');
    if (!amount || amount <= 0) return;
    setAbsenceGranting(p => ({ ...p, [id]: true }));
    try {
      const res = await apiFetch(`/alerts/${id}/grant-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      const data = await res.json();
      if (data.success) setAbsenceAlerts(a => a.filter(x => x.id !== id));
    } finally {
      setAbsenceGranting(p => ({ ...p, [id]: false }));
    }
  };

  const handleAbsenceDismiss = async (id) => {
    setAbsenceDismissing(p => ({ ...p, [id]: true }));
    try {
      await apiFetch(`/alerts/${id}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'OTHER' }),
      });
      setAbsenceAlerts(a => a.filter(x => x.id !== id));
    } finally {
      setAbsenceDismissing(p => ({ ...p, [id]: false }));
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith('/notifications') && notifUnread > 0) {
      apiFetch('/notifications/mark-all-read', { method: 'PATCH' }).catch(() => {});
      setNotifUnread(0);
    }
  }, [location.pathname, notifUnread]);

  const ROUTE_SECTION = {
    '/payments':    'PAYMENT',
    '/comments':    'COMMENT',
    '/attendance':  'ATTENDANCE',
    '/exams':       'EXAM',
    '/homework':    'HOMEWORK',
    '/todos':       'TODO',
    '/print-materials': 'PRINT_REQUEST',
  };

  useEffect(() => {
    setSidebarOpen(false);
    const section = Object.entries(ROUTE_SECTION).find(([route]) => location.pathname.startsWith(route))?.[1];
    if (section && counts[section] > 0) {
      clearSection(section);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!localStorage.getItem('user')) return <Navigate to="/login" replace />;

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {}
    localStorage.removeItem('user');
    navigate('/login');
  };

  const path = location.pathname;

  // Sidebar uses theme.primary as background → always white text
  const SB = {
    text: 'rgba(255,255,255,0.70)',
    textActive: 'rgba(255,255,255,1)',
    hover: 'rgba(255,255,255,0.10)',
    active: 'rgba(255,255,255,0.16)',
    divider: 'rgba(255,255,255,0.10)',
    sectionLabel: 'rgba(255,255,255,0.38)',
    headerOverlay: 'rgba(0,0,0,0.18)',
  };

  // ── MenuItem ────────────────────────────────────────────────────────────────
  const MenuItem = ({ icon, label, to, exact, badge }) => {
    const isActive = exact ? path === to : path.startsWith(to);

    return (
      <button
        onClick={() => { navigate(to); setSidebarOpen(false); }}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left relative"
        style={isActive
          ? { backgroundColor: SB.active, color: SB.textActive }
          : { color: SB.text }
        }
        onMouseEnter={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = SB.hover;
            e.currentTarget.style.color = SB.textActive;
          }
        }}
        onMouseLeave={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = '';
            e.currentTarget.style.color = SB.text;
          }
        }}
      >
        {/* Active left accent bar */}
        {isActive && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: 20,
              borderRadius: '0 3px 3px 0',
              backgroundColor: 'rgba(255,255,255,0.9)',
            }}
          />
        )}
        <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <span className="flex-1 truncate" style={{ fontSize: 13.5, fontWeight: isActive ? 600 : 500 }}>
          {label}
        </span>
        {badge > 0 && (
          <span
            className="flex items-center justify-center font-bold text-white"
            style={{
              fontSize: 10,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: '#ef4444',
              padding: '0 4px',
              flexShrink: 0,
            }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );
  };

  // ── NavGroup: collapsible section ───────────────────────────────────────────
  const NavGroup = ({ icon, label, groupKey, childPaths, totalBadge, defaultOpen, children }) => {
    const isChildActive = childPaths.some(p => path.startsWith(p));

    const [open, setOpen] = useState(() => {
      const stored = localStorage.getItem(`navgroup_${groupKey}`);
      if (stored !== null) return stored === 'true';
      return defaultOpen !== undefined ? defaultOpen : isChildActive;
    });

    useEffect(() => {
      if (isChildActive && !open) {
        setOpen(true);
        localStorage.setItem(`navgroup_${groupKey}`, 'true');
      }
    }, [isChildActive]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = () => {
      const next = !open;
      setOpen(next);
      localStorage.setItem(`navgroup_${groupKey}`, String(next));
    };

    return (
      <div style={{ marginBottom: 2 }}>
        {/* Section header button */}
        <button
          onClick={toggle}
          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md transition-all"
          style={{ color: isChildActive ? 'rgba(255,255,255,0.65)' : SB.sectionLabel }}
          onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = isChildActive ? 'rgba(255,255,255,0.65)' : SB.sectionLabel; }}
        >
          <span style={{ fontSize: 12 }}>{icon}</span>
          <span
            className="flex-1 text-left font-bold tracking-widest"
            style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em' }}
          >
            {label}
          </span>
          {!open && totalBadge > 0 && (
            <span
              className="flex items-center justify-center font-bold text-white"
              style={{ fontSize: 9, minWidth: 16, height: 16, borderRadius: 8, backgroundColor: '#ef4444', padding: '0 3px', flexShrink: 0 }}
            >
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
          <motion.span
            animate={{ rotate: open ? 90 : 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            style={{ fontSize: 8, opacity: 0.45, display: 'inline-block', lineHeight: 1, flexShrink: 0 }}
          >
            ▶
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <div
                className="ml-2 pl-2 py-0.5 space-y-0.5"
                style={{ borderLeft: `1px solid ${SB.divider}` }}
              >
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // User initials avatar
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  const roleLabel = user?.role
    ? user.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '';

  return (
    <div style={{ backgroundColor: theme.bgPage, minHeight: '100vh' }}>
      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* ── SIDEBAR ───────────────────────────────────────────────────────── */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 flex flex-col z-30 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{
          backgroundColor: theme.primary,
          boxShadow: '2px 0 20px rgba(0,0,0,0.15)',
        }}
      >
        {/* ── Brand header ─────────────────────────────────────── */}
        <div
          style={{
            backgroundColor: SB.headerOverlay,
            borderBottom: `1px solid ${SB.divider}`,
            padding: '16px 16px 14px',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <img
              src="/genius-logo.png"
              alt="Genius English Courses"
              className="h-8 object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-md transition-colors"
              style={{ color: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.backgroundColor = ''; }}
              aria-label="Close menu"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* User profile row */}
          {user && (
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1.5px solid rgba(255,255,255,0.25)' }}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white leading-tight truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs leading-tight truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {roleLabel}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Language switcher row */}
        <div
          className="px-4 py-2"
          style={{ borderBottom: `1px solid ${SB.divider}` }}
        >
          <LanguageSwitcher style={{}} />
        </div>

        {/* ── Navigation ───────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>

          {/* Core — always visible */}
          <MenuItem icon="📊" label={t('nav.dashboard')} to="/dashboard" exact />
          <MenuItem icon="👥" label={t('nav.students')}  to="/students" />
          <MenuItem icon="👨‍🏫" label={t('nav.teachers')} to="/teachers" />
          <MenuItem icon="📚" label={t('nav.groups')}    to="/groups" />
          <MenuItem icon="🗓️" label="Schedule"           to="/schedule" />

          {/* Divider */}
          <div style={{ height: 1, backgroundColor: SB.divider, margin: '10px 4px 8px' }} />

          {/* Finance */}
          <NavGroup
            icon="💰"
            label="Finance"
            groupKey="finance"
            childPaths={['/payments', '/expenditures']}
            totalBadge={counts['PAYMENT'] || 0}
          >
            <MenuItem icon="💰" label={t('nav.payments')}     to="/payments"     badge={counts['PAYMENT'] || 0} />
            <MenuItem icon="📉" label={t('nav.expenditures')} to="/expenditures" />
          </NavGroup>

          {/* Academics */}
          <NavGroup
            icon="🎓"
            label="Academics"
            groupKey="academics"
            childPaths={['/attendance', '/homework', '/exams', '/certificates', '/vocab-checks', '/readiness-report']}
            totalBadge={(counts['ATTENDANCE'] || 0) + (counts['HOMEWORK'] || 0) + (counts['EXAM'] || 0)}
          >
            <MenuItem icon="📝" label={t('nav.attendance')}   to="/attendance"                 badge={counts['ATTENDANCE'] || 0} />
            <MenuItem icon="⚠️" label="Discrepancies"         to="/attendance/discrepancies" />
            <MenuItem icon="📓" label={t('nav.homework')}     to="/homework"                   badge={counts['HOMEWORK'] || 0} />
            <MenuItem icon="📋" label="Vocab Checks"          to="/vocab-checks" />
            <MenuItem icon="🎯" label="Readiness Report"      to="/readiness-report" />
            <MenuItem icon="📋" label={t('nav.exams')}        to="/exams"                      badge={counts['EXAM'] || 0} />
            <MenuItem icon="🏆" label={t('nav.certificates')} to="/certificates" />
          </NavGroup>

          {/* Learning Tools */}
          <NavGroup
            icon="🧪"
            label="Learning Tools"
            groupKey="learning"
            childPaths={['/test-bank', '/srs-items', '/srs-analytics', '/srs-planner', '/placement', '/library', '/syllabus', '/print-materials']}
            totalBadge={counts['PRINT_REQUEST'] || 0}
          >
            <MenuItem icon="🧪" label="Test Bank"                 to="/test-bank" />
            <MenuItem icon="🔁" label="SRS Item Bank"            to="/srs-items" />
            <MenuItem icon="📈" label="SRS Analytics"            to="/srs-analytics" />
            <MenuItem icon="🔀" label="SRS Planner"              to="/srs-planner" />
            <MenuItem icon="🎯" label="Placement Test"           to="/placement" />
            <MenuItem icon="🗂️" label="Library"                   to="/library" />
            <MenuItem icon="📋" label="Syllabus"                  to="/syllabus" />
            <MenuItem icon="🖨️" label={t('nav.printMaterials')}  to="/print-materials" badge={counts['PRINT_REQUEST'] || 0} />
          </NavGroup>

          {/* Communication */}
          <NavGroup
            icon="💬"
            label="Communication"
            groupKey="comms"
            childPaths={['/comments', '/telegram', '/notifications', '/nps', '/ai-bot']}
            totalBadge={(counts['COMMENT'] || 0) + notifUnread}
          >
            <MenuItem icon="💬" label={t('nav.comments')}    to="/comments"              badge={counts['COMMENT'] || 0} />
            <MenuItem icon="📱" label={t('nav.telegram')}    to="/telegram" />
            {user?.role === 'MASTER_ADMIN' && (
              <MenuItem icon="🤖" label="AI Bot"             to="/ai-bot" />
            )}
            <MenuItem icon="🔔" label="Notifications"        to="/notifications"          badge={notifUnread} />
            <MenuItem icon="⭐" label="NPS / Satisfaction"   to="/nps" />
          </NavGroup>

          {/* Operations */}
          <NavGroup
            icon="⚙️"
            label="Operations"
            groupKey="operations"
            childPaths={['/todos', '/audit-log', '/at-risk']}
            totalBadge={(counts.TODO || 0) + absenceAlerts.length}
            defaultOpen={false}
          >
            <MenuItem icon="✅" label="TO DO" to="/todos" badge={counts.TODO} />
            <MenuItem icon="⚠️" label="At-Risk Students" to="/at-risk" />
            {/* Absence Warnings button */}
            <button
              onClick={() => setAbsencePanelOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left relative"
              style={{
                color: absenceAlerts.length > 0 ? '#fbbf24' : SB.text,
                backgroundColor: absenceAlerts.length > 0 ? 'rgba(251,191,36,0.12)' : '',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(251,191,36,0.18)'; e.currentTarget.style.color = '#fbbf24'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = absenceAlerts.length > 0 ? 'rgba(251,191,36,0.12)' : ''; e.currentTarget.style.color = absenceAlerts.length > 0 ? '#fbbf24' : SB.text; }}
            >
              <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>🏫</span>
              <span className="flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 500 }}>Absence Warnings</span>
              {absenceAlerts.length > 0 && (
                <span className="flex items-center justify-center font-bold text-white" style={{ fontSize: 10, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: '#f59e0b', padding: '0 4px', flexShrink: 0 }}>
                  {absenceAlerts.length > 99 ? '99+' : absenceAlerts.length}
                </span>
              )}
            </button>
            <MenuItem icon="🔍" label="Audit Log" to="/audit-log" />
          </NavGroup>

        </nav>

        {/* ── Theme switcher ────────────────────────────────────── */}
        <div style={{ borderTop: `1px solid ${SB.divider}`, padding: '12px 16px' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: SB.sectionLabel, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 8 }}>
            Theme
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {Object.values(THEMES).map(th => (
              <button
                key={th.id}
                onClick={() => setThemeId(th.id)}
                title={th.name}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: th.swatch,
                  border: themeId === th.id ? '2px solid white' : '2px solid rgba(255,255,255,0.25)',
                  boxShadow: themeId === th.id ? '0 0 0 2px rgba(255,255,255,0.4)' : '0 1px 3px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
              />
            ))}
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>
              {THEMES[themeId]?.name}
            </span>
          </div>
        </div>

        {/* ── Logout ───────────────────────────────────────────── */}
        <div style={{ borderTop: `1px solid ${SB.divider}`, padding: '12px 16px 16px' }}>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 rounded-lg transition-all font-medium"
            style={{
              height: 40,
              fontSize: 13.5,
              color: 'rgba(255,255,255,0.75)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* ── Absence Warnings Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {absencePanelOpen && (
          <>
            <motion.div
              key="abs-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setAbsencePanelOpen(false)}
            />
            <motion.div
              key="abs-panel"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.18 }}
              className="fixed z-50 overflow-hidden"
              style={{
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%', maxWidth: 480,
                maxHeight: '80vh',
                borderRadius: '1rem',
                backgroundColor: 'white',
                boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* Panel header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #fde68a', background: '#fffbeb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>🏫</span>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#92400e' }}>Absence Warnings</span>
                  {absenceAlerts.length > 0 && (
                    <span style={{ background: '#f59e0b', color: '#fff', fontSize: '0.7rem', fontWeight: 700, borderRadius: 9999, padding: '0.1rem 0.5rem' }}>
                      {absenceAlerts.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setAbsencePanelOpen(false)}
                  style={{ color: '#92400e', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', borderRadius: '0.375rem', lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fcd34d33'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Panel body */}
              <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
                {absenceAlerts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#9ca3af' }}>
                    <p style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</p>
                    <p style={{ fontWeight: 500 }}>No absence warnings</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {absenceAlerts.map(alert => {
                      const studentName = alert.student
                        ? `${alert.student.user.firstName} ${alert.student.user.lastName}`
                        : null;
                      const busy = absenceDismissing[alert.id];
                      const creditBusy = absenceGranting[alert.id];
                      return (
                        <div
                          key={alert.id}
                          style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '4px solid #f59e0b', borderRadius: '0.625rem', padding: '0.75rem 1rem' }}
                        >
                          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', lineHeight: 1.4 }}>
                            {studentName ? (
                              <>
                                <a
                                  href={`/students/${alert.studentId}`}
                                  onClick={() => setAbsencePanelOpen(false)}
                                  style={{ color: '#92400e', textDecoration: 'underline', cursor: 'pointer' }}
                                >{studentName}</a>
                                {alert.message.slice(studentName.length)}
                              </>
                            ) : alert.message}
                          </p>
                          <p style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '0.2rem' }}>
                            {alert.group?.name} · {new Date(alert.createdAt).toLocaleDateString('en-GB')}
                          </p>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.65rem' }}>
                            <input
                              type="number"
                              min="1"
                              placeholder="Credit amount (UZS)"
                              value={absenceCreditInputs[alert.id] || ''}
                              onChange={e => setAbsenceCreditInputs(p => ({ ...p, [alert.id]: e.target.value }))}
                              style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #fcd34d', width: 160, outline: 'none' }}
                            />
                            <button
                              disabled={creditBusy || !absenceCreditInputs[alert.id]}
                              onClick={() => handleAbsenceGrantCredit(alert.id)}
                              style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.3rem 0.85rem', borderRadius: 9999, background: '#f59e0b', color: '#fff', border: 'none', cursor: (creditBusy || !absenceCreditInputs[alert.id]) ? 'not-allowed' : 'pointer', opacity: (creditBusy || !absenceCreditInputs[alert.id]) ? 0.6 : 1 }}
                            >
                              {creditBusy ? '...' : '💳 Grant Credit'}
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => handleAbsenceDismiss(alert.id)}
                              style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.3rem 0.75rem', borderRadius: 9999, background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Page content ──────────────────────────────────────────────────── */}
      <div
        className="lg:ml-64 h-screen overflow-y-auto"
        style={{ scrollbarGutter: 'stable', backgroundColor: theme.bgPage }}
      >
        {/* Mobile top bar */}
        <div
          className="lg:hidden sticky top-0 z-10 flex items-center justify-between px-4 shadow-lg"
          style={{ height: 56, backgroundColor: theme.primary }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md transition-colors"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; e.currentTarget.style.backgroundColor = ''; }}
            aria-label="Open menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <img
            src="/genius-logo.png"
            alt="Genius English Courses"
            className="h-7 object-contain"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <div className="w-8" />
        </div>

        <Outlet />
      </div>

      {/* AI Assistant chat panel */}
      <AiAssistantPanel />
    </div>
  );
}
